import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { ArrowLeft } from 'lucide-react';
import SEO from './SEO';

const MAX_ADDITIONAL_IMAGES = 12;

const ProjectDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { projects } = useData();
  const project = projects.find(p => p.id === parseInt(id));
  const additionalImages = project?.additionalImages?.slice(0, MAX_ADDITIONAL_IMAGES) ?? [];

  if (!project) {
    return <div className="text-center py-20" data-testid="project-not-found-message">Project not found</div>;
  }

  // Schema.org Structured Data for the Project
  const projectSchema = {
    "@context": "https://schema.org",
    "@type": project.category === "Film" ? "Movie" : "CreativeWork",
    "name": project.title,
    "description": project.description,
    "image": project.image,
    "dateCreated": project.year,
    "author": {
      "@type": "Person",
      "name": "Leon Krykhtin"
    },
    "genre": project.category
  };

  return (
    <div className="animate-in fade-in duration-700 pb-20">
      <SEO 
        title={project.title}
        description={project.fullDescription || project.description}
        image={project.image}
        url={`/project/${project.id}`}
        type="article"
        schema={projectSchema}
      />
      {/* Back Button */}
      <button 
        onClick={() => navigate(-1)}
        className="group flex items-center text-sm uppercase tracking-widest font-medium mb-12 hover:text-neutral-500 transition-colors"
        data-testid="project-back-button"
      >
        <ArrowLeft className="w-4 h-4 mr-2 transition-transform group-hover:-translate-x-1" />
        Back
      </button>

      {/* Header Info */}
      <div className="mb-12 border-b border-black pb-8">
        <div className="flex flex-col md:flex-row md:items-baseline justify-between mb-4">
          <h1 className="text-4xl md:text-6xl font-bold uppercase tracking-tighter mb-4 md:mb-0" data-testid="project-title">
            {project.title}
          </h1>
          <div className="flex gap-8 text-sm font-mono text-neutral-500" data-testid="project-metadata">
            <span>{project.year}</span>
            <span>{project.category}</span>
          </div>
        </div>
        <p className="text-xl md:text-2xl leading-relaxed max-w-2xl font-light text-neutral-800" data-testid="project-summary">
          {project.description}
        </p>
      </div>

      {/* Main Media */}
      {project.videos && project.videos.length > 0 ? (
        <div className="space-y-8 mb-16">
          {project.videos.map((vid, idx) => (
            <div key={idx} className="w-full aspect-video bg-neutral-100 shadow-sm overflow-hidden" data-testid={`project-video-${idx + 1}`}>
              <iframe
                width="100%"
                height="100%"
                src={vid}
                title={`${project.title} ${idx + 1}`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              ></iframe>
            </div>
          ))}
        </div>
      ) : (
        <div className="w-full aspect-video bg-neutral-100 mb-16 shadow-sm overflow-hidden" data-testid="project-primary-media">
          {project.video ? (
              <iframe
                width="100%"
                height="100%"
                src={project.video}
                title={project.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              ></iframe>
          ) : (
              <img 
                src={project.image} 
                alt={project.title}
                className="w-full h-full object-cover"
              />
          )}
        </div>
      )}

      {/* Full Description & Context */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-20">
        <div className="md:col-span-1">
          <h3 className="text-sm font-bold uppercase tracking-widest mb-4">About the work</h3>
        </div>
        <div className="md:col-span-2 text-lg leading-relaxed text-neutral-600 max-w-4xl" data-testid="project-description">
          {(project.fullDescription || project.description).split('\n\n').map((paragraph, index) => (
            <p key={index} className="mb-6 last:mb-0 whitespace-pre-wrap">{paragraph}</p>
          ))}
          
          {project.link && (
            <div className="mt-8">
              <a 
                href={project.link}
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center text-sm font-bold uppercase tracking-widest border-b-2 border-black pb-1 hover:text-neutral-600 hover:border-neutral-600 transition-colors"
                data-testid="project-external-link"
              >
                View Project Page
                <span className="ml-2">→</span>
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Additional Images Grid */}
      {additionalImages.length > 0 && (
        <div className="space-y-8" data-testid="project-additional-images-gallery">
          {additionalImages.map((img, index) => (
            <div key={index} className="w-full bg-neutral-100 overflow-hidden shadow-sm" data-testid={`project-additional-image-${index + 1}`}>
              <img 
                src={img} 
                alt={`${project.title} detail ${index + 1}`}
                className="w-full h-auto object-cover hover:scale-105 transition-transform duration-700"
                loading="lazy"
                data-testid={`project-additional-image-content-${index + 1}`}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProjectDetails;
